import { Instance, SnapshotOut, types } from 'mobx-state-tree';

import {
  DeptRestructureApi,
  TDeleteDeptRestructureMapResult,
  TDeleteDeptRestructureResult,
  TGetDeptRestructureMapsResult,
  TGetDeptRestructureResult,
  TGetDeptRestructuresResult,
  TPostDeptRestructureMapResult,
  TPostDeptRestructureResult,
  TUpdateDeptRestructureMapResult,
  TUpdateDeptRestructureResult,
} from '../../services';
import {
  createDeptRestructure,
  DeptRestructure,
  IDeptRestructure,
  IDeptRestructureSnapshot,
} from '../dept-restructure/DeptRestructure';
import {
  DeptRestructureMap,
  IDeptRestructureMap,
  IDeptRestructureMapSnapshot,
} from '../dept-restructure/DeptRestructureMap';
import { withEnvironment } from '../extensions/with-environment';
import { withRootStore } from '../extensions/with-root-store';
import { createPagination, IPagination, IPaginationSnapshot } from '../pagination/Pagination';

/**
 * # DeptRestructureStore
 *
 * 조직 개편 이벤트 및 부서 매핑 관리 스토어
 */
export const DeptRestructureStore = types
  .model('DeptRestructureStore')
  // --------------------------------------------------------------------------
  .props({
    pagination: createPagination(),
    deptRestructures: types.optional(types.array(DeptRestructure), []),
    deptRestructure: createDeptRestructure(),
    deptRestructureMaps: types.optional(types.array(DeptRestructureMap), []),
  })
  .extend(withRootStore)
  .extend(withEnvironment)
  // eslint-disable-line @typescript-eslint/no-unused-vars
  .views((self) => ({}))
  // --------------------------------------------------------------------------
  // MUTATEs - 모델 상태를 변경
  .actions((self) => ({
    setPagination: (pagination: IPaginationSnapshot) => {
      self.pagination = pagination as IPagination;
    },
    setDeptRestructures: (deptRestructures: IDeptRestructure[]) =>
      self.deptRestructures.replace(deptRestructures),
    appendDeptRestructures: (deptRestructures: IDeptRestructure[]) => {
      // 중복 제거를 위해 기존 SID 목록 생성
      const existingSids = new Set(self.deptRestructures.map((r) => r.restructureSid));
      // 새로운 데이터 중 중복되지 않은 것만 추가
      const newItems = deptRestructures.filter((r) => !existingSids.has(r.restructureSid));
      self.deptRestructures.push(...newItems);
    },
    setDeptRestructure: (deptRestructure: IDeptRestructure) =>
      (self.deptRestructure = deptRestructure),
    resetDeptRestructure: () => (self.deptRestructure = {} as IDeptRestructure),
    setDeptRestructureMaps: (maps: IDeptRestructureMap[]) => self.deptRestructureMaps.replace(maps),
    resetPagination: () => {
      self.pagination = { size: 25, page: 1 } as IPagination;
    },
    resetDeptRestructures: () => {
      self.deptRestructures.clear();
    },
  }))
  // --------------------------------------------------------------------------
  // REQUESTs - 서비스 요청 및 기타 인터페이스 요청
  .actions((self) => ({
    gets: async (
      append = false,
      filters?: {
        restructureTypeCd?: number;
        startDate?: string;
        endDate?: string;
      },
    ) => {
      const api: DeptRestructureApi = new DeptRestructureApi(self.environment.api);
      const result: TGetDeptRestructuresResult = await api.gets(self.pagination, filters);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        if (append) {
          self.appendDeptRestructures(result.deptRestructures);
        } else {
          self.setDeptRestructures(result.deptRestructures);
        }
        self.setPagination(result.pagination);
      } else {
        console.error(result.kind);
      }
    },
    get: async (restructureSid: number) => {
      const api: DeptRestructureApi = new DeptRestructureApi(self.environment.api);
      const result: TGetDeptRestructureResult = await api.get(restructureSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setDeptRestructure(result.deptRestructure);
      } else {
        console.error(result.kind);
      }
    },
    post: async (data: IDeptRestructureSnapshot) => {
      const api: DeptRestructureApi = new DeptRestructureApi(self.environment.api);
      const result: TPostDeptRestructureResult = await api.post(data);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setDeptRestructure(result.deptRestructure);
      } else {
        console.error(result.kind);
      }
    },
    update: async (data: IDeptRestructureSnapshot) => {
      const api: DeptRestructureApi = new DeptRestructureApi(self.environment.api);
      const result: TUpdateDeptRestructureResult = await api.update(data);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setDeptRestructure(result.deptRestructure);
      } else {
        console.error(result.kind);
      }
    },
    delete: async (restructureSid: number) => {
      const api: DeptRestructureApi = new DeptRestructureApi(self.environment.api);
      const result: TDeleteDeptRestructureResult = await api.delete(restructureSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        return;
      } else {
        console.error(result.kind);
      }
    },
    /**
     * 바로 실행 (DRAFT → EXECUTED)
     */
    executeDirect: async (restructureSid: number) => {
      const api: DeptRestructureApi = new DeptRestructureApi(self.environment.api);
      const result: TPostDeptRestructureResult = await api.executeDirect(restructureSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setDeptRestructure(result.deptRestructure);
        return result;
      } else {
        console.error(result.kind);
        return result;
      }
    },
    /**
     * 승인 요청 (DRAFT → PENDING)
     */
    submit: async (restructureSid: number) => {
      const api: DeptRestructureApi = new DeptRestructureApi(self.environment.api);
      const result: TPostDeptRestructureResult = await api.submit(restructureSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setDeptRestructure(result.deptRestructure);
        return result;
      } else {
        console.error(result.kind);
        return result;
      }
    },
    /**
     * 승인 (PENDING → APPROVED)
     */
    approve: async (restructureSid: number, comment?: string) => {
      const api: DeptRestructureApi = new DeptRestructureApi(self.environment.api);
      const result: TPostDeptRestructureResult = await api.approve(restructureSid, comment);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setDeptRestructure(result.deptRestructure);
        return result;
      } else {
        console.error(result.kind);
        return result;
      }
    },
    /**
     * 반려 (PENDING → REJECTED)
     */
    reject: async (restructureSid: number, reason: string) => {
      const api: DeptRestructureApi = new DeptRestructureApi(self.environment.api);
      const result: TPostDeptRestructureResult = await api.reject(restructureSid, reason);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setDeptRestructure(result.deptRestructure);
        return result;
      } else {
        console.error(result.kind);
        return result;
      }
    },
    /**
     * 실행 (APPROVED → EXECUTED)
     */
    execute: async (restructureSid: number) => {
      const api: DeptRestructureApi = new DeptRestructureApi(self.environment.api);
      const result: TPostDeptRestructureResult = await api.execute(restructureSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setDeptRestructure(result.deptRestructure);
        return result;
      } else {
        console.error(result.kind);
        return result;
      }
    },
    /**
     * 취소 (DRAFT/PENDING → CANCELLED)
     */
    cancel: async (restructureSid: number) => {
      const api: DeptRestructureApi = new DeptRestructureApi(self.environment.api);
      const result: TPostDeptRestructureResult = await api.cancel(restructureSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setDeptRestructure(result.deptRestructure);
        return result;
      } else {
        console.error(result.kind);
        return result;
      }
    },
    // --------------------------------------------------------------------------
    // DeptRestructureMap (부서 매핑) 관련 액션
    // --------------------------------------------------------------------------
    getMaps: async (restructureSid: number) => {
      const api: DeptRestructureApi = new DeptRestructureApi(self.environment.api);
      const result: TGetDeptRestructureMapsResult = await api.getMaps(restructureSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        self.setDeptRestructureMaps(result.deptRestructureMaps);
      } else {
        console.error(result.kind);
      }
    },
    postMap: async (restructureSid: number, data: IDeptRestructureMapSnapshot) => {
      const api: DeptRestructureApi = new DeptRestructureApi(self.environment.api);
      const result: TPostDeptRestructureMapResult = await api.postMap(restructureSid, data);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        return;
      } else {
        console.error(result.kind);
      }
    },
    updateMap: async (restructureSid: number, data: IDeptRestructureMapSnapshot) => {
      const api: DeptRestructureApi = new DeptRestructureApi(self.environment.api);
      const result: TUpdateDeptRestructureMapResult = await api.updateMap(restructureSid, data);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        return;
      } else {
        console.error(result.kind);
      }
    },
    deleteMap: async (restructureSid: number, mapSid: number) => {
      const api: DeptRestructureApi = new DeptRestructureApi(self.environment.api);
      const result: TDeleteDeptRestructureMapResult = await api.deleteMap(restructureSid, mapSid);
      self.rootStore.responseStore.getResponseResult(result);
      if (result.kind === 'ok') {
        return;
      } else {
        console.error(result.kind);
      }
    },
  }));

// --------------------------------------------------------------------------
type TDeptRestructureStore = Instance<typeof DeptRestructureStore>;
type TDeptRestructureStoreSnapshot = SnapshotOut<typeof DeptRestructureStore>;

export interface IDeptRestructureStore extends TDeptRestructureStore {}
export type TDeptRestructureStoreKeys = keyof TDeptRestructureStoreSnapshot & string;
export interface IDeptRestructureStoreSnapshot extends TDeptRestructureStoreSnapshot {}
export const createDeptRestructureStore = () =>
  types.optional(DeptRestructureStore, {} as TDeptRestructureStore);
